import React, { useEffect, useState } from 'react';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { usePermission } from '../../hooks/usePermission';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemDecorator,
  ListItemContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useColorScheme,
  Input,
  Button,
  Drawer,
} from '@mui/joy';
import {
  MagnifyingGlass,
  X,
  SquaresFour as DashboardOutlined,
  Gear as SettingsOutlined,
  CurrencyDollar as AttachMoneyOutlined,
  UsersThree as SupervisorAccountOutlined,
  Handshake as HandshakeOutlined,
  List as MenuOutlined,
  CaretLeft as ChevronLeftOutlined,
  CaretDown as ExpandMoreOutlined,
  Tray as InboxOutlined,
  ChatCircle as ChatOutlined,
  Megaphone as CampaignOutlined,
  EnvelopeOpen as MarkEmailReadOutlined,
  Mailbox as LocalPostOfficeOutlined,
  TrendUp as TrendingUpOutlined,
  Briefcase as BusinessCenterOutlined,
  Flask as ScienceOutlined,
  ChartBar as AssessmentOutlined,
  Files as PagesOutlined,
  Sparkle as AutoAwesomeOutlined,
  // Marketing icons
  QrCode as QrCode2Outlined,
  Calculator as CalculateOutlined,
  MapPin as LocationOnOutlined,
  Share as ShareOutlined,
  Article as ArticleOutlined,
  Image as ImageOutlined,
  Star as StarOutlined,
  // Sales icons
  Users as PeopleOutlined,
  Kanban as ViewKanbanOutlined,
  ClipboardText as AssignmentOutlined,
  FileText as DescriptionOutlined,
  // Operations icons
  CheckSquare as CheckBoxOutlined,
  Ruler as StraightenOutlined,
  Calendar as CalendarTodayOutlined,
  Receipt as ReceiptOutlined,
  Truck as LocalShippingOutlined,
  Wrench as BuildOutlined,
  ListChecks as ChecklistOutlined,
  ListBullets as ListAltOutlined,
  Toolbox as HandymanOutlined,
  CurrencyCircleDollar as MonetizationOnOutlined,
  // Accounting icons
  Bank as AccountBalanceOutlined,
  Wallet as AccountBalanceWallet,
  // Advanced icons
  Eye as RemoveRedEyeOutlined,
  CloudLightning as ThunderstormOutlined,
  // Team Management icons
  UsersThree as GroupsOutlined,
  UserPlus as PersonAddOutlined,
  GraduationCap as SchoolOutlined,
  IdentificationBadge as BadgeOutlined,
  Briefcase as WorkOutlined,
  // Dark mode icons
  Sun as LightModeOutlined,
  Moon as DarkModeOutlined,
  // Support icon
  Question as HelpOutlineOutlined,
} from '@phosphor-icons/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isAffiliate, isSuperAdmin } from '../../utils/affiliate';

interface LeftSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  /** Pixels to push the sidebar down for the impersonation bar. */
  topOffset?: number;
}

const MOBILE_QUERY = '(max-width: 959.95px)';

export const LeftSidebar: React.FC<LeftSidebarProps> = ({
  isCollapsed: isCollapsedProp,
  onToggle,
  mobileOpen = false,
  onMobileClose,
  topOffset = 0,
}) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_QUERY).matches : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const { user } = useAuth();
  const location = useLocation();
  const scope = `${location.pathname}:${isCollapsedProp}`;
  const [searchState, setSearchState] = useState({ scope, value: '' });
  const search = searchState.scope === scope ? searchState.value : '';
  const setSearch = (value: string) => setSearchState({ scope, value });
  const [groupState, setGroupState] = useState<{ scope: string; values: Record<string, boolean> }>({ scope, values: {} });
  const expandedGroups = groupState.scope === scope ? groupState.values : {};
  const matchesSearch = (label: string) => label.toLowerCase().includes(search.trim().toLowerCase());
  const { mode, setMode } = useColorScheme();
  const showScheduling = useFeatureFlag('scheduling_enabled');
  const showAutomations = useFeatureFlag('automations_enabled');
  const showTeamManagement = useFeatureFlag('team_management');
  const showStormTracking = useFeatureFlag('storm_tracking');
  const showNewWebsite = useFeatureFlag('website');
  const { can } = usePermission();

  // Route-prefix → RBAC resource (mirrors backend users/rbac.py). Items
  // whose route maps to a resource the role can't VIEW are hidden; routes
  // with no mapping stay visible (server-side middleware is authoritative).
  const routeResource: Array<[string, string]> = [
    ['/inbox', 'inbox'],
    ['/chat', 'teamchat'],
    ['/marketing/website-builder', 'website_builder'],
    ['/marketing/reviews', 'reviews'],
    ['/marketing/local-seo', 'local_seo'],
    ['/marketing/social-media', 'social_media'],
    ['/marketing/forms', 'forms'],
    ['/marketing/qr-codes', 'qr_codes'],
    ['/marketing/roof-quote-widget', 'roof_quote'],
    ['/marketing/content', 'content_marketing'],
    ['/marketing/media-library', 'media_library'],
    ['/marketing/email-marketing', 'email_marketing'],
    ['/marketing/direct-mail', 'direct_mail'],
    ['/crm/contacts', 'contacts'],
    ['/crm/sales-desk', 'contacts'],
    ['/crm/pipeline', 'pipeline'],
    ['/crm/proposals', 'proposals'],
    ['/crm/contracts', 'contracts'],
    ['/jobs', 'jobs'],
    ['/tasks', 'tasks'],
    ['/measurements', 'measurements'],
    ['/scheduling', 'scheduling'],
    ['/billing', 'billing'],
    ['/distributors', 'distributors'],
    ['/policy-copilot', 'policy_copilot'],
    ['/reports', 'reporting'],
    ['/reporting', 'reporting'],
    ['/subcontractors', 'subcontractors'],
    ['/checklists', 'checklists'],
    ['/accounting', 'accounting'],
    ['/hr', 'hr'],
    ['/training', 'training'],
  ];
  const canViewRoute = (path: string): boolean => {
    const canonicalPath = path.replace(/^\/operations(?=\/)/, '');
    const match = routeResource.find(([prefix]) => canonicalPath === prefix || canonicalPath.startsWith(prefix + '/'));
    return match ? can(match[1], 'view') : true;
  };
  const showRoofQuoteWidget = useFeatureFlag('roof_quote_widget');
  const showEmailMarketing = useFeatureFlag('email_marketing');
  const showDirectMail = useFeatureFlag('direct_mail');
  // const showAccounting = useFeatureFlag('accounting');
  // const showMarketing = useFeatureFlag('marketing');
  // const showInbox = useFeatureFlag('inbox');

  // Debug logging for feature flag
  //console.log('Accounting feature flag value:', showAccounting);

  const isActivePage = (path: string) => {
    const { pathname } = location;
    if (!(pathname === path || pathname.startsWith(path + '/'))) return false;
    // Longest match wins: a parent item (e.g. Billing) is not active when a
    // more specific nav item (e.g. Recurring Invoices under /billing/recurring)
    // matches the current path.
    const allPaths = [
      ...mainNavigationItems.map((i) => i.path),
      ...navigationGroups.flatMap((g) => g.items.map((i) => i.path)),
      ...additionalItems.map((i) => i.path),
    ].filter(Boolean);
    return !allPaths.some(
      (other) =>
        other !== path &&
        other.startsWith(path + '/') &&
        (pathname === other || pathname.startsWith(other + '/'))
    );
  };

  const closeNavigation = () => {
    setSearch('');
    onMobileClose?.();
  };

  const isCollapsed = isMobile ? false : isCollapsedProp;
  const sidebarWidth = isCollapsed ? 64 : 240;

  // Main navigation items (top level)
  const mainNavigationItems = [
    {
      label: 'Dashboard',
      icon: <DashboardOutlined />,
      path: '/dashboard',
      show: true,
    },
    {
      label: 'Inbox',
      icon: <InboxOutlined />,
      path: '/inbox',
      show: true,
    },
    {
      label: 'Team Chat',
      icon: <ChatOutlined />,
      path: '/chat',
      show: true,
    },
  ];

  // Navigation groups with sub-items
  const navigationGroups = [
    {
      label: 'Marketing',
      icon: <CampaignOutlined />,
      // Admins always; role-holders when their matrix grants any marketing area
      show: !!user?.is_organization_admin || (!!user?.role && [
        'website_builder', 'reviews', 'local_seo', 'social_media', 'forms',
        'qr_codes', 'roof_quote', 'content_marketing', 'media_library',
        'email_marketing', 'direct_mail',
      ].some((r) => can(r, 'view'))),
      items: [
        { label: 'Website', icon: <AutoAwesomeOutlined />, path: '/marketing/website-builder', show: showNewWebsite },
        { label: 'Reviews', icon: <StarOutlined />, path: '/marketing/reviews', show: true },
        { label: 'Local SEO', icon: <LocationOnOutlined />, path: '/marketing/local-seo', show: true },
        { label: 'Social Media', icon: <ShareOutlined />, path: '/marketing/social-media', show: true },
        { label: 'Forms', icon: <ListAltOutlined />, path: '/marketing/forms', show: true },
        { label: 'QR Codes', icon: <QrCode2Outlined />, path: '/marketing/qr-codes', show: true },
        { label: 'Roof Quote Widget', icon: <CalculateOutlined />, path: '/marketing/roof-quote-widget', show: showRoofQuoteWidget },
        { label: 'Content Marketing', icon: <ArticleOutlined />, path: '/marketing/content', show: true },
        { label: 'Media Library', icon: <ImageOutlined />, path: '/marketing/media-library', show: true },
        { label: 'Email Marketing', icon: <MarkEmailReadOutlined />, path: '/marketing/email-marketing', show: showEmailMarketing },
        { label: 'Direct Mail', icon: <LocalPostOfficeOutlined />, path: '/marketing/direct-mail', show: showDirectMail },
        { label: 'Marketing Settings', icon: <SettingsOutlined />, path: '/marketing/settings', show: true },
      ],
    },
    {
      label: 'Sales',
      icon: <TrendingUpOutlined />,
      show: true,
      items: [
        { label: 'Contacts', icon: <PeopleOutlined />, path: '/crm/contacts', show: true },
        { label: 'Sales Desk', icon: <TrendingUpOutlined />, path: '/crm/sales-desk', show: can('inbox', 'view') },
        { label: 'Sales Pipeline', icon: <ViewKanbanOutlined />, path: '/crm/pipeline', show: true },
        { label: 'Proposals', icon: <DescriptionOutlined />, path: '/crm/proposals', show: true },
        { label: 'Contracts', icon: <AssignmentOutlined />, path: '/crm/contracts', show: true },
        { label: 'Settings', icon: <SettingsOutlined />, path: '/crm/settings', show: true },
      ],
    },
    {
      label: 'Operations',
      icon: <BusinessCenterOutlined />,
      show: true,
      items: [
        { label: 'Jobs', icon: <ViewKanbanOutlined />, path: '/operations/jobs', show: true },
        { label: 'Tasks', icon: <CheckBoxOutlined />, path: '/operations/tasks', show: true },
        { label: 'Measurements', icon: <StraightenOutlined />, path: '/operations/measurements', show: true },
        { label: 'Scheduling', icon: <CalendarTodayOutlined />, path: '/operations/scheduling', show: true },
        { label: 'My Meetings', icon: <PeopleOutlined />, path: '/scheduling/meetings', show: showScheduling },
        { label: 'Team Scheduling', icon: <SupervisorAccountOutlined />, path: '/scheduling/teams', show: showScheduling },
        { label: 'Billing', icon: <ReceiptOutlined />, path: '/operations/billing', show: true },
        { label: 'Recurring Invoices', icon: <ReceiptOutlined />, path: '/operations/billing/recurring', show: true },
        { label: 'Commissions', icon: <MonetizationOnOutlined />, path: '/operations/commissions', show: true },
        { label: 'Distributors', icon: <LocalShippingOutlined />, path: '/operations/distributors', show: true },
        { label: 'Subcontractors', icon: <BuildOutlined />, path: '/operations/subcontractors', show: true },
        { label: 'Work Orders', icon: <BuildOutlined />, path: '/operations/work-orders', show: true },
        { label: 'Maintenance', icon: <HandymanOutlined />, path: '/maintenance', show: true },
        { label: 'Checklists', icon: <ChecklistOutlined />, path: '/checklists', show: true },
      ],
    },
    {
      label: 'Team Management',
      icon: <GroupsOutlined />,
      show: showTeamManagement,
      items: [
        { label: 'Team Directory', icon: <BadgeOutlined />, path: '/hr/team', show: true },
        { label: 'My Employee File', icon: <BadgeOutlined />, path: '/hr/my-employee-file', show: true },
        { label: 'Recruiting', icon: <WorkOutlined />, path: '/hr/recruiting', show: user?.is_hr || user?.is_organization_admin },
        { label: 'Onboarding', icon: <PersonAddOutlined />, path: '/hr/onboarding', show: user?.is_hr || user?.is_organization_admin },
        // Everyone gets their own training; the library behind it is HR admin
        // (IsHRUser), so showing it to a crew member only ever 403s.
        { label: 'My Training', icon: <SchoolOutlined />, path: '/training', show: true },
        { label: 'Training Library', icon: <SchoolOutlined />, path: '/hr/training', show: user?.is_hr || user?.is_organization_admin },
        { label: 'Team Settings', icon: <SettingsOutlined />, path: '/hr/settings', show: user?.is_hr || user?.is_organization_admin },
      ],
    },
    {
      label: 'Accounting',
      icon: <AccountBalanceOutlined />,
      show: true,
      items: [
        { label: 'Dashboard', icon: <DashboardOutlined />, path: '/accounting', show: true },
        { label: 'Financial Analytics', icon: <TrendingUpOutlined />, path: '/accounting/analytics', show: true },
        { label: 'Chart of Accounts', icon: <AccountBalanceOutlined />, path: '/accounting/chart-of-accounts', show: true },
        { label: 'Journal Entries', icon: <ReceiptOutlined />, path: '/accounting/journal-entries', show: true },
        { label: 'Reports', icon: <AssessmentOutlined />, path: '/accounting/reports', show: true },
        { label: 'Bank Connections', icon: <AccountBalanceWallet />, path: '/accounting/banking', show: true },
        { label: 'Bank Transactions', icon: <ReceiptOutlined />, path: '/accounting/banking/transactions', show: true },
        { label: 'Reconciliation', icon: <CheckBoxOutlined />, path: '/accounting/banking/reconciliation', show: true },
        { label: 'Transaction Rules', icon: <ListAltOutlined />, path: '/accounting/banking/rules', show: true },
        { label: 'Settings', icon: <SettingsOutlined />, path: '/accounting/settings', show: true },
      ],
    },
    {
      label: 'Advanced',
      icon: <ScienceOutlined />,
      show: true,
      items: [
        { label: 'Automations', icon: <AutoAwesomeOutlined />, path: '/advanced/automations', show: showAutomations },
        { label: 'Hawkeye™', icon: <RemoveRedEyeOutlined />, path: '/hawkeye', show: true },
        { label: 'Policy Copilot™', icon: <AssignmentOutlined />, path: '/policy-copilot', show: true },
        { label: 'Storm Tracker™', icon: <ThunderstormOutlined />, path: '/advanced/storm-tracker', show: showStormTracking },
      ],
    },
  ];

  // Additional navigation items (bottom section)
  const additionalItems = [
    {
      label: 'Reports',
      icon: <AssessmentOutlined />,
      path: '/reports',
      show: true,
    },
    {
      label: 'Pages',
      icon: <PagesOutlined />,
      path: '/pages',
      show: true,
    },
    {
      label: 'Settings',
      icon: <SettingsOutlined />,
      path: '/settings',
      show: true,
    },
    {
      label: 'Affiliate Dashboard',
      icon: <AttachMoneyOutlined />,
      path: '/affiliate-dashboard',
      show: isAffiliate(user),
    },
    {
      label: 'Become an Affiliate',
      icon: <HandshakeOutlined />,
      path: '/affiliate-signup',
      show: !isAffiliate(user) && !isSuperAdmin(user),
    },
    {
      label: 'Support',
      icon: <HelpOutlineOutlined />,
      path: '/support',
      show: true,
    },
  ];

  const hasSearchResults = mainNavigationItems.some(item => item.show && canViewRoute(item.path) && matchesSearch(item.label))
    || additionalItems.some(item => item.show && canViewRoute(item.path) && matchesSearch(item.label))
    || navigationGroups.some(group => group.show && group.items.some(item => item.show !== false && canViewRoute(item.path) && (matchesSearch(group.label) || matchesSearch(item.label))));

  const sidebar = (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{
        width: { xs: 280, md: sidebarWidth },
        height: isMobile ? '100dvh' : `calc(100dvh - ${topOffset}px)`,
        backgroundColor: 'background.surface',
        borderRight: '1px solid',
        borderColor: 'divider',
        transition: 'width 0.2s ease, transform 0.25s ease',
        position: isMobile ? 'relative' : 'fixed',
        left: 0,
        top: isMobile ? 0 : `${topOffset}px`,
        zIndex: { xs: 1300, md: 1000 },
        transform: {
          xs: 'none',
          md: 'translateX(0)',
        },
        boxShadow: { xs: mobileOpen ? 'lg' : 'none', md: 'none' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header with logo and toggle */}
      <Box
        sx={{
          px: 2,
          py: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#7013f5',
          color: 'white',
          height: '84px', // Match top bar total height (64px + 20px padding)
        }}
      >
        {!isCollapsed && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1
          }}>
            <img
              src="/ApexOS-Logo-White.png"
              alt="ApexOS"
              style={{ height: '64px', width: 'auto' }}
            />
          </Box>
        )}

        <IconButton
          variant="plain"
          size="md"
          onClick={isMobile ? onMobileClose : onToggle}
          aria-label={isMobile ? 'Close navigation' : isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={isMobile ? 'Close navigation' : isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          sx={{
            minHeight: 40,
            minWidth: 40,
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
            '& .MuiSvgIcon-root': {
              fontSize: '1.5rem',
            },
          }}
        >
          {isCollapsed ?
            <MenuOutlined color="white" /> :
            <ChevronLeftOutlined color="white" />
          }
        </IconButton>
      </Box>

      {!isCollapsed && <Box sx={{ px: 1.5, pt: 2, pb: 0.5 }}>
        <Input size="sm" placeholder="Find a page" value={search} onChange={event => setSearch(event.target.value)}
          startDecorator={<MagnifyingGlass size={18} />} slotProps={{ input: { 'aria-label': 'Find a page' } }}
          endDecorator={search ? <IconButton size="sm" variant="plain" aria-label="Clear navigation search" onClick={() => setSearch('')}><X size={16} /></IconButton> : null} />
      </Box>}
      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
        {!hasSearchResults && <Box sx={{ p: 2 }}><Typography level="body-sm">No pages match “{search}”.</Typography><Button size="sm" variant="plain" onClick={() => setSearch('')}>Clear search</Button></Box>}
        {/* Main Navigation Items */}
        <List size="sm" sx={{ '--ListItem-radius': '8px', mb: 1 }}>
          {mainNavigationItems.filter((item) => item.show && canViewRoute(item.path) && matchesSearch(item.label)).map((item) => {
            if (!item.show || !canViewRoute(item.path) || !matchesSearch(item.label)) return null;
            const isActive = isActivePage(item.path);

            return (
              <ListItem key={item.path}>
                <ListItemButton
                  variant={isActive ? 'soft' : 'plain'}
                  color={isActive ? 'primary' : 'neutral'}
                  component={RouterLink}
                  to={item.path}
                  onClick={closeNavigation}
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={item.label}
                  title={isCollapsed ? item.label : undefined}
                  sx={{
                    py: 1.5,
                    px: isCollapsed ? 0 : 2,
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    minHeight: 40,
                  }}
                >
                  <ListItemDecorator
                    sx={{
                      minInlineSize: isCollapsed ? 'auto' : undefined,
                    }}
                  >
                    {item.icon}
                  </ListItemDecorator>
                  {!isCollapsed && (
                    <ListItemContent>
                      <Typography level="body-sm" fontWeight={isActive ? 'lg' : 'md'}>
                        {item.label}
                      </Typography>
                    </ListItemContent>
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {/* Navigation Groups with Accordions */}
        {!isCollapsed && (
          <Box>
            {navigationGroups.map((group) => {
              if (!group.show) return null;
              // Hide a group entirely when RBAC filters out every item —
              // an empty accordion header is worse than no header
              if (!group.items.some((item) => item.show !== false && canViewRoute(item.path) && (matchesSearch(group.label) || matchesSearch(item.label)))) return null;

              const hasActiveItem = group.items.some(item => isActivePage(item.path));

              return (
                <Accordion key={group.label} expanded={search.trim() ? true : (expandedGroups[group.label] ?? hasActiveItem)} onChange={(_, expanded) => setGroupState({ scope, values: { ...expandedGroups, [group.label]: expanded } })}>
                  <AccordionSummary
                    indicator={<ExpandMoreOutlined />}
                    sx={{
                      py: 1,
                      px: 2,
                      minHeight: 40,
                      backgroundColor: hasActiveItem ? 'primary.softBg' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'neutral.softHoverBg',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {group.icon}
                      <Typography level="body-sm" fontWeight={hasActiveItem ? 'lg' : 'md'}>
                        {group.label}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <List size="sm" sx={{ '--ListItem-radius': '6px' }}>
                      {group.items.filter((item) => item.show !== false && canViewRoute(item.path) && (matchesSearch(group.label) || matchesSearch(item.label))).map((item) => {
                        // For the Accounting Dashboard, use exact match to prevent it from
                        // staying highlighted on sub-pages
                        const isActive = item.path === '/accounting'
                          ? location.pathname === item.path
                          : isActivePage(item.path);

                        return (
                          <ListItem key={item.path}>
                            <ListItemButton
                              variant={isActive ? 'soft' : 'plain'}
                              color={isActive ? 'primary' : 'neutral'}
                              component={RouterLink}
                  to={item.path}
                  onClick={closeNavigation}
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={item.label}
                  title={isCollapsed ? item.label : undefined}
                              sx={{
                                py: 1,
                                px: 4,
                                minHeight: 36,
                              }}
                            >
                              <ListItemDecorator>
                                {item.icon}
                              </ListItemDecorator>
                              <ListItemContent>
                                <Typography level="body-sm" fontWeight={isActive ? 'lg' : 'md'}>
                                  {item.label}
                                </Typography>
                              </ListItemContent>
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}

        {/* Collapsed Group Items (show as simple buttons) */}
        {isCollapsed && (
          <List size="sm" sx={{ '--ListItem-radius': '8px', mb: 1 }}>
            {navigationGroups.map((group) => {
              if (!group.show) return null;
              if (!group.items.some((item) => item.show !== false && canViewRoute(item.path) && (matchesSearch(group.label) || matchesSearch(item.label)))) return null;
              const hasActiveItem = group.items.some(item => isActivePage(item.path));

              // Show group icon, clicking opens first item or most relevant item
              const firstItem = group.items.find(item => item.show !== false && canViewRoute(item.path) && (matchesSearch(group.label) || matchesSearch(item.label)));
              if (!firstItem) return null;

              return (
                <ListItem key={group.label}>
                  <ListItemButton
                    variant={hasActiveItem ? 'soft' : 'plain'}
                    color={hasActiveItem ? 'primary' : 'neutral'}
                    component={RouterLink}
                    to={firstItem.path}
                    onClick={closeNavigation}
                    aria-label={`${group.label}: ${firstItem.label}`}
                    title={`${group.label}: ${firstItem.label}`}
                    sx={{
                      py: 1.5,
                      px: 0,
                      justifyContent: 'center',
                      minHeight: 40,
                    }}
                  >
                    <ListItemDecorator>
                      {group.icon}
                    </ListItemDecorator>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}

        {/* Bottom Section - Additional Items */}
        <Divider sx={{ my: 1 }} />
        <List size="sm" sx={{ '--ListItem-radius': '8px' }}>
          {additionalItems.map((item) => {
            if (!item.show || !canViewRoute(item.path) || !matchesSearch(item.label)) return null;
            const isActive = isActivePage(item.path);

            return (
              <ListItem key={item.path}>
                <ListItemButton
                  variant={isActive ? 'soft' : 'plain'}
                  color={isActive ? 'primary' : 'neutral'}
                  component={RouterLink}
                  to={item.path}
                  onClick={closeNavigation}
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={item.label}
                  title={isCollapsed ? item.label : undefined}
                  sx={{
                    py: 1.5,
                    px: isCollapsed ? 0 : 2,
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    minHeight: 40,
                  }}
                >
                  <ListItemDecorator
                    sx={{
                      minInlineSize: isCollapsed ? 'auto' : undefined,
                    }}
                  >
                    {item.icon}
                  </ListItemDecorator>
                  {!isCollapsed && (
                    <ListItemContent>
                      <Typography level="body-sm" fontWeight={isActive ? 'lg' : 'md'}>
                        {item.label}
                      </Typography>
                    </ListItemContent>
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {/* Dark Mode Toggle */}
        <Divider sx={{ my: 1 }} />
        <List size="sm" sx={{ '--ListItem-radius': '8px' }}>
          <ListItem>
            <ListItemButton
              variant="plain"
              color="neutral"
              onClick={() => setMode(mode === 'light' ? 'dark' : 'light')}
              aria-label={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              title={isCollapsed ? (mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode') : undefined}
              sx={{
                py: 1.5,
                px: isCollapsed ? 0 : 2,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                minHeight: 40,
              }}
            >
              <ListItemDecorator
                sx={{
                  minInlineSize: isCollapsed ? 'auto' : undefined,
                }}
              >
                {mode === 'light' ? <DarkModeOutlined /> : <LightModeOutlined />}
              </ListItemDecorator>
              {!isCollapsed && (
                <ListItemContent>
                  <Typography level="body-sm" fontWeight="md">
                    {mode === 'light' ? 'Dark Mode' : 'Light Mode'}
                  </Typography>
                </ListItemContent>
              )}
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );
  return isMobile ? <Drawer open={mobileOpen} onClose={onMobileClose} anchor="left" slotProps={{ content: { sx: { width: 280, maxWidth: 'calc(100vw - 32px)' } } }}>{sidebar}</Drawer> : sidebar;
};
